import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Add, Cancel, Delete, Edit, Save, Block } from '@mui/icons-material'
import { Alert, Box, Button, Card, CardActions, CardContent, CardHeader, Divider, FormControlLabel, Grid, Stack, Switch, TableContainer } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../components/GRTooltip/GRTooltip'
import { GameCardContent } from '../../../features/game/components/GameCard/GameCard'
import { TrackedGameBlueprint } from '../../../features/live-events/types/TrackedGame'
import { displaySnackBar } from '../../../features/snackbar'
import usePage from '../../../hooks/usePage'
import { useRequiredParams } from '../../../hooks/useRequiredParams'
import PageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { TranslationMap } from '../../../types/TranslationMap'
import {
  useGetTrackedGameQuery,
  useCreateLiveEventBlueprintMutation,
  useDeleteLiveEventBlueprintMutation,
  useUpdateLiveEventBlueprintMutation,
} from '../../api/internal'
import { useGetTrackedBlueprintsQuery } from '../../api/markets'
import { ActionButton } from '../../components/ActionButton/ActionButton'
import { BackButton } from '../../components/BackButton/BackButton'
import { BlueprintFamilySearch } from '../../components/BlueprintFamilySearch/BlueprintFamilySearch'
import { languageServiceNameResolver } from '../../components/BlueprintFamilySearch/blueprintFamilyGrouping'
import FloatingToolbar from '../../components/FloatingToolbar/FloatingToolbar'
import { InputTranslator } from '../../components/InputTranslator/InputTranslator'
import { CustomerBlueprintLink } from '../../features/live-events-tracking/components/CustomerBlueprintLink/CustomerBlueprintLink'
import { acceptedEventTypeIds } from '../../features/live-events-tracking/helpers/acceptedEventTypes'
import { useGameBlueprintEventTypes } from '../../features/live-events-tracking/hooks/useGameBlueprintEventTypes'
import { InputTranslatorType } from '../../types/InputTranslatorType'

/**
 * Props for the EventBlueprintsEditPage component
 */
type EventBlueprintsEditPageProps = {
  children?: ReactNode
}

type EventBlueprintFormValues = {
  names: TranslationMap
  discontinued: boolean
  descriptions: TranslationMap
  blueprintFamilyId?: string
}

// react-hook-form's reset skips fields set to undefined, so "no family" is '' in the form and only
// becomes undefined when it is sent.
const emptyFormValues = (): EventBlueprintFormValues => ({
  names: { en: '', ja: '', zh: '' },
  discontinued: false,
  descriptions: { en: '', ja: '', zh: '' },
  blueprintFamilyId: '',
})

/**
 * Page component for editing live event blueprints for a tracked game.
 *
 * This component provides functionality to:
 * - Add new live event blueprints with multilingual names
 * - Edit existing blueprints
 * - Delete blueprints with confirmation
 * - Display a table of all blueprints for the game
 *
 * The component handles both creation mode (when no blueprintId is provided)
 * and edit mode (when blueprintId is provided in the URL params).
 *
 * @param props - Component props
 * @param props.children - Optional child components
 * @returns JSX element representing the event blueprints edit page
 */
export const EventBlueprintsEditPage: FC<EventBlueprintsEditPageProps> = ({ children }) => {
  usePage(PageService.getInternalPageWithId('internal-event-blueprints-edit'))
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { trackedGameId, blueprintId } = useRequiredParams<{ trackedGameId: string; blueprintId: string }>()
  const getTrackedGameQuery = useGetTrackedGameQuery(trackedGameId, { skip: !trackedGameId })
  const trackedGame = getTrackedGameQuery.data
  const { data: trackedBlueprints } = useGetTrackedBlueprintsQuery()
  const [createLiveEventBlueprint, { isLoading: isCreating }] = useCreateLiveEventBlueprintMutation()
  const [deleteLiveEventBlueprint, { isLoading: isDeleting }] = useDeleteLiveEventBlueprintMutation()
  const [updateLiveEventBlueprint, { isLoading: isUpdating }] = useUpdateLiveEventBlueprintMutation()
  const isLoading = isCreating || isDeleting || isUpdating

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<TrackedGameBlueprint> | undefined>()

  /**
   * Default form values based on the current blueprint being edited
   */
  const defaultValues = useMemo(() => {
    const blueprint = trackedGame?.blueprints?.find((blueprint) => blueprint.blueprintId === blueprintId)
    return {
      names: blueprint?.names || { en: '', ja: '', zh: '' },
      discontinued: blueprint?.discontinued || false,
      descriptions: blueprint?.descriptions || { en: '', ja: '', zh: '' },
      blueprintFamilyId: blueprint?.blueprintFamilyId || '',
    }
  }, [blueprintId, trackedGame?.blueprints])

  const form = useForm<EventBlueprintFormValues>({
    defaultValues: defaultValues,
  })

  const { control, getValues, reset, watch } = form

  // Watch the names field to validate English name requirement
  const watchedNames = watch('names')
  const watchedBlueprintFamilyId = watch('blueprintFamilyId')

  // Pointing a blueprint at a Blueprint Family that does not accept the types its own events
  // use is never blocked, so the types that would start reporting as mismatches are named here.
  const { typeIds: blueprintEventTypeIds, isLoading: blueprintEventTypesLoading } = useGameBlueprintEventTypes(trackedGameId, blueprintId)
  // Saving while those types are still loading would skip the warning, so it waits for them.
  const waitingForMismatchData = !!watchedBlueprintFamilyId && blueprintEventTypesLoading
  const mismatchedTypeNames = useMemo(() => {
    const blueprintFamily = trackedBlueprints?.find((candidate) => candidate.id === watchedBlueprintFamilyId)
    const accepted = acceptedEventTypeIds(blueprintFamily?.eventTypes)
    if (!blueprintFamily || accepted.length === 0) return []
    return blueprintEventTypeIds.filter((typeId) => !accepted.includes(typeId)).map((typeId) => languageServiceNameResolver.eventTypeName(typeId) || typeId)
  }, [blueprintEventTypeIds, trackedBlueprints, watchedBlueprintFamilyId])

  /**
   * Validates that the English name is provided
   */
  const isEnglishNameValid = useMemo(() => {
    return watchedNames?.en && watchedNames.en.trim().length > 0
  }, [watchedNames?.en])

  // The page stays mounted between edit and add (e.g. browser Back from an edit), so leaving an edit
  // has to empty the form. A fresh Add page is left alone so refetches don't wipe what is typed.
  const previousBlueprintIdRef = useRef(blueprintId)
  useEffect(() => {
    if (blueprintId) {
      reset(defaultValues)
    } else if (previousBlueprintIdRef.current) {
      reset(emptyFormValues())
    }
    previousBlueprintIdRef.current = blueprintId
  }, [blueprintId, reset, defaultValues])

  /**
   * Handles the creation of a new live event blueprint
   *
   * Validates that a tracked game ID is available, then creates the blueprint
   * with the current form values. Resets the form on success.
   */
  const handleAddBlueprint = async () => {
    const names = getValues('names')
    const discontinued = getValues('discontinued')
    const descriptions = getValues('descriptions')
    const blueprintFamilyId = getValues('blueprintFamilyId')

    if (!trackedGameId) {
      throw new Error('No tracked game ID available')
    }

    try {
      await createLiveEventBlueprint({
        gameId: trackedGameId,
        names: names,
        discontinued: discontinued,
        descriptions: descriptions,
        blueprintFamilyId: blueprintFamilyId || undefined,
      }).unwrap()

      reset(emptyFormValues())
    } catch (error) {
      displaySnackBar({ message: t('internal-live-events:failed_to_create_live_event_blueprint'), severity: 'error', open: true })
    }
  }

  /**
   * Initiates the deletion process for a blueprint
   *
   * Opens a confirmation dialog with details about the blueprint to be deleted
   *
   * @param row - The blueprint row data
   * @param rowIndex - Index of the row in the table
   */
  const handleRemoveBlueprint = useCallback(
    async (row: TrackedGameBlueprint, rowIndex: number) => {
      setConfirmDialogData({
        title: t('internal-live-events:delete_blueprint_confirm_dialog_title'),
        confirmText: t('internal-live-events:delete_blueprint_confirm_dialog_confirm_text', { name: row.names.en }),
        actionText: t('internal-common:delete'),
        destructiveAction: true,
        data: row,
      })
      setConfirmDialogOpen(true)
    },
    [t]
  )

  /**
   * Executes the deletion of a blueprint after confirmation
   *
   * @param confirmData - Confirmation dialog data containing the blueprint to delete
   */
  const handleConfirmDelete = async (confirmData?: ConfirmDialogData<TrackedGameBlueprint>) => {
    if (!confirmData?.data?.blueprintId) {
      displaySnackBar({ message: t('internal-live-events:cannot_delete_blueprint_missing_id'), severity: 'error', open: true })
      return
    }

    try {
      await deleteLiveEventBlueprint(confirmData.data.blueprintId).unwrap()
      displaySnackBar({ message: t('internal-live-events:blueprint_deleted_successfully'), severity: 'success', open: true })
    } catch (error) {
      displaySnackBar({ message: t('internal-live-events:failed_to_delete_blueprint'), severity: 'error', open: true })
    }
  }

  /**
   * Handles the closing of the confirmation dialog
   *
   * @param confirmData - Confirmation dialog data if user confirmed the action
   */
  const handleConfirmDialogClose = (confirmData?: ConfirmDialogData<TrackedGameBlueprint>) => {
    setConfirmDialogOpen(false)
    if (confirmData) {
      handleConfirmDelete(confirmData)
    }
  }

  /**
   * Navigates to the edit page for a specific blueprint
   *
   * @param row - The blueprint row data
   * @param rowIndex - Index of the row in the table
   */
  const handleEditBlueprint = useCallback(
    async (row: TrackedGameBlueprint, rowIndex: number) => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      navigate(`/internal/live-events-tracking/events/${trackedGameId}/blueprints/edit/${row.blueprintId}`)
    },
    [navigate, trackedGameId]
  )

  /**
   * Helper function to get the blueprint family name from the blueprint ID
   */
  const getBlueprintFamilyName = useCallback(
    (id?: string): string | undefined => {
      if (!id || !trackedBlueprints) {
        return undefined
      }

      const blueprint = trackedBlueprints.find((bp) => bp.id === id)
      if (blueprint) {
        return blueprint.names.en || blueprint.names.ja || blueprint.names.zh || 'Unnamed'
      }

      return undefined
    },
    [trackedBlueprints]
  )

  /**
   * Custom props passed to the table component for action handlers
   */
  const customProps = useMemo(
    () => ({
      handleRemoveBlueprint,
      handleEditBlueprint,
      getBlueprintFamilyName,
    }),
    [handleRemoveBlueprint, handleEditBlueprint, getBlueprintFamilyName]
  )

  const blueprints: TrackedGameBlueprint[] = trackedGame?.blueprints || []

  const tableColumns: GRTableColumn<TrackedGameBlueprint, typeof customProps>[] = useMemo(
    () => [
      {
        labelAccessor: () => <Trans i18nKey="internal-common:name" />,
        accessor: ({ row }) => row.names.en,
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left' } },
        sortable: true,
        sortOrder: SortOrder.DESC,
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:description" />,
        accessor: ({ row }) => {
          const description = row.descriptions?.en || ''
          if (!description) return ''

          return (
            <GRTooltip content={description}>
              <Box
                sx={{
                  maxWidth: '300px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {description}
              </Box>
            </GRTooltip>
          )
        },
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left' } },
        sortable: true,
      },
      {
        labelAccessor: () => <Trans i18nKey="live-events:market_level_blueprint" />,
        accessor: ({ row, customTableProps }) => {
          const blueprintName = customTableProps?.getBlueprintFamilyName(row.blueprintFamilyId)
          return blueprintName || ''
        },
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left' } },
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => customTableProps?.getBlueprintFamilyName(row.blueprintFamilyId) || '',
      },
      {
        labelAccessor: () => <Trans i18nKey="live-events:live_event_evolution_type_discontinued" />,
        accessor: ({ row }) => {
          return row.discontinued ? <Block color="error" /> : undefined
        },
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 90 } },
        sortable: true,
        sortAccessor: ({ row }) => (row.discontinued ? 1 : 0),
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:actions" />,
        headerCellProps: { sx: { width: 160 } },
        accessor: ({ row, rowIndex, customTableProps }) => (
          <Stack direction="row" spacing={1} justifyContent="center">
            <CustomerBlueprintLink gameId={trackedGameId} blueprintId={row.blueprintId} />
            <ActionButton onClick={() => customTableProps?.handleEditBlueprint(row, rowIndex)} disabled={isDeleting}>
              <Edit fontSize="small" />
            </ActionButton>
            <ActionButton color="warning" onClick={() => customTableProps?.handleRemoveBlueprint(row, rowIndex)} disabled={isDeleting}>
              <Delete fontSize="small" />
            </ActionButton>
          </Stack>
        ),
      },
    ],
    [isDeleting, trackedGameId]
  )

  /**
   * Handles cancellation of the edit operation
   *
   * Resets the form and navigates back to the blueprints list
   */
  const handleCancel = () => {
    reset(emptyFormValues())
    navigate(`/internal/live-events-tracking/events/${trackedGameId}/blueprints/edit`)
  }

  /**
   * Handles saving changes to an existing blueprint
   *
   * Validates that a blueprint ID is available, then updates the blueprint
   * with the current form values. Shows success/error messages and navigates
   * back to the blueprints list on success.
   */
  const handleSaveBlueprint = async () => {
    const names = getValues('names')
    const discontinued = getValues('discontinued')
    const descriptions = getValues('descriptions')
    const blueprintFamilyId = getValues('blueprintFamilyId')

    if (!blueprintId) {
      displaySnackBar({ message: t('internal-live-events:cannot_save_blueprint_missing_id'), severity: 'error', open: true })
      return
    }

    try {
      await updateLiveEventBlueprint({
        id: blueprintId,
        names: names,
        discontinued: discontinued,
        descriptions: descriptions,
        blueprintFamilyId: blueprintFamilyId || undefined,
      }).unwrap()

      displaySnackBar({ message: t('internal-live-events:blueprint_updated_successfully'), severity: 'success', open: true })
      reset(emptyFormValues())
      navigate(`/internal/live-events-tracking/events/${trackedGameId}/blueprints/edit`)
    } catch (error) {
      displaySnackBar({ message: t('internal-live-events:failed_to_update_blueprint'), severity: 'error', open: true })
    }
  }

  const [columns, setColumns] = useState<GRTableColumn<TrackedGameBlueprint, typeof customProps>[]>(tableColumns)

  /**
   * Handles updates to table columns configuration
   *
   * @param updatedColumns - The updated column configuration
   */
  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<TrackedGameBlueprint, typeof customProps>[]) => {
    setColumns(updatedColumns)
  }, [])

  return (
    <Box>
      <Box sx={{ marginBottom: 2 }}>{trackedGame && <GameCardContent game={trackedGame.game} disableGameLink />}</Box>
      <FormProvider {...form}>
        <Card>
          <CardHeader title={blueprintId ? t('internal-live-events:edit_live_event_blueprint') : t('internal-live-events:add_new_live_event_blueprint')} />
          <Divider />
          <CardContent>
            <Grid container spacing={2} flexWrap="wrap">
              <Grid xs={12} item>
                <Controller
                  name="names"
                  control={control}
                  render={({ field }) => (
                    <InputTranslator
                      label={t('internal-common:name')}
                      value={field.value}
                      inputType={InputTranslatorType.Text}
                      dialogTitle={t('internal-live-events:live_event_blueprint_name')}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} item>
                <Controller
                  name="descriptions"
                  control={control}
                  render={({ field }) => (
                    <InputTranslator
                      label={t('internal-live-events:description')}
                      value={field.value}
                      inputType={InputTranslatorType.WYSIWYG}
                      dialogTitle={t('internal-live-events:description')}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} item>
                <Controller
                  name="blueprintFamilyId"
                  control={control}
                  render={({ field }) => (
                    <BlueprintFamilySearch
                      value={field.value}
                      onChange={field.onChange}
                      label={t('live-events:market_level_blueprint')}
                      showDiscontinuedToggle={true}
                    />
                  )}
                />
              </Grid>

              {mismatchedTypeNames.length > 0 && (
                <Grid xs={12} item>
                  <Alert severity="warning">{t('internal-live-events:blueprint_family_mismatch_warning', { types: mismatchedTypeNames.join(', ') })}</Alert>
                </Grid>
              )}

              <Grid xs={2} item sx={{ display: 'flex', justifyContent: 'center' }}>
                <Controller
                  name="discontinued"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                      label={t('live-events:live_event_evolution_type_discontinued')}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <CardActions sx={{ justifyContent: 'flex-end', padding: 1 }}>
            {!blueprintId ? (
              <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAddBlueprint} disabled={!isEnglishNameValid || isCreating}>
                {isCreating ? t('internal-live-events:adding') : t('internal-live-events:add')}
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" color="primary" startIcon={<Cancel />} onClick={handleCancel} disabled={isUpdating}>
                  {t('internal-common:cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  onClick={handleSaveBlueprint}
                  disabled={!isEnglishNameValid || isUpdating || waitingForMismatchData}
                >
                  {isUpdating ? t('internal-live-events:saving') : t('internal-common:save')}
                </Button>
              </Stack>
            )}
          </CardActions>
        </Card>
      </FormProvider>

      <Card sx={{ marginTop: 3 }}>
        <CardHeader title={t('internal-live-events:blueprints_for_this_game')} />
        <Divider />
        <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
          <TableContainer component={Box}>
            <GRTable
              rows={blueprints}
              columns={columns}
              rowIdKey="blueprintId"
              isLoading={isLoading}
              gridlines={true}
              striped
              hoverable
              onColumnsUpdated={handleColumnsUpdate}
              noRowsLabel={t('internal-live-events:no_blueprints_found')}
              customProps={customProps}
            />
          </TableContainer>
        </CardContent>
      </Card>

      <FloatingToolbar>
        <BackButton mb={0} url={`/internal/live-events-tracking/events/${trackedGameId}`} />
      </FloatingToolbar>

      <ConfirmDialog open={confirmDialogOpen} onClose={handleConfirmDialogClose} confirmDialogData={confirmDialogData} />
    </Box>
  )
}
